import React from "react";

import {
  FaFilter,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  BILLING_ITEM_TYPE_OPTIONS,
  PAYMENT_STATUS_LABELS,
  RENEWAL_STATUS_LABELS,
} from "../data/subscriptionOptions";

const SORT_OPTIONS = [
  {
    value: "attention",
    label: "Priorités d’abord",
  },
  {
    value: "start_desc",
    label: "Plus récents",
  },
  {
    value: "end_asc",
    label: "Échéances proches",
  },
  {
    value: "amount_desc",
    label: "Montants décroissants",
  },
  {
    value: "association_asc",
    label: "Associations A → Z",
  },
];

export default function SubscriptionFilters({
  filters,
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
        <div className="min-w-0 flex-1">
          <label
            htmlFor="subscription-search"
            className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
          >
            Rechercher
          </label>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              id="subscription-search"
              type="search"
              value={filters.search}
              onChange={(event) =>
                filters.setSearch(
                  event.target.value,
                )
              }
              placeholder="Club, produit, prestation, référence..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-indigo-500 dark:focus:bg-slate-900 dark:focus:ring-indigo-950"
            />

            {filters.search && (
              <button
                type="button"
                onClick={() =>
                  filters.setSearch("")
                }
                className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
                aria-label="Effacer la recherche"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <FilterSelect
            label="Type"
            value={filters.itemType}
            onChange={filters.setItemType}
          >
            <option value="all">
              Tous les types
            </option>

            {BILLING_ITEM_TYPE_OPTIONS.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ),
            )}
          </FilterSelect>

          <FilterSelect
            label="Paiement"
            value={filters.paymentStatus}
            onChange={filters.setPaymentStatus}
          >
            <option value="all">
              Tous les paiements
            </option>

            {Object.entries(
              PAYMENT_STATUS_LABELS,
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Renouvellement"
            value={filters.renewalStatus}
            onChange={
              filters.setRenewalStatus
            }
          >
            <option value="all">
              Tous les renouvellements
            </option>

            {Object.entries(
              RENEWAL_STATUS_LABELS,
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Statut"
            value={filters.lifecycleStatus}
            onChange={
              filters.setLifecycleStatus
            }
          >
            <option value="all">
              Tous les statuts
            </option>

            {filters.lifecycleStatusOptions.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status}
                </option>
              ),
            )}
          </FilterSelect>

          <FilterSelect
            label="Trier par"
            value={filters.sortBy}
            onChange={filters.setSortBy}
          >
            {SORT_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </FilterSelect>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <FaFilter />

          <span>
            <strong className="text-slate-900 dark:text-white">
              {filters.totalItems}
            </strong>{" "}
            résultat
            {filters.totalItems > 1 ? "s" : ""}
          </span>

          {filters.activeFilterCount > 0 && (
            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
              {filters.activeFilterCount} filtre
              {filters.activeFilterCount > 1
                ? "s"
                : ""}{" "}
              actif
              {filters.activeFilterCount > 1
                ? "s"
                : ""}
            </span>
          )}
        </div>

        {filters.activeFilterCount > 0 && (
          <button
            type="button"
            onClick={filters.resetFilters}
            className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            <FaTimes />

            Réinitialiser
          </button>
        )}
      </div>
    </section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="w-full min-w-[150px] rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:focus:border-indigo-500 dark:focus:bg-slate-900 dark:focus:ring-indigo-950"
      >
        {children}
      </select>
    </label>
  );
}